import { useEffect, useRef } from "react";
import {
  producingColor,
  waitingColor,
  unplannedColor,
  untaggedColor,
  plantLossColor,
  plannedColor,
  qualityLossColor,
  darkGreen,
  uncollectedColor,
  textOnProducing,
  textOnWaiting,
  textOnUnplanned,
  textOnUnknown,
  textOnPlanned,
  textOnNotScheduled,
  textOnUncollected,
  changeoverColor,
} from "./colors";
import {
  PageNameEnum,
  PnlCategoriesEnumWithOptionalCategories,
  PnlCategoriesEnum,
  PnlCategoryLabelsEnum,
} from "types/enums";
import { useMediaQuery } from "@mui/material";
import { css } from "styled-components/macro";
import { getBrowserInfo } from "utils/cssUtils";

export const MB = 1024 ** 2; // 1 MB = 1024 * 1024 Bytes
// Sentry.io
export const sentryDsn =
  "https://25f4de0993b042f1ba159811cd3aa595@sentry.io/1197934";

export const noop = () => null;
export const useMount = fn => {
  useEffect(fn, []); // eslint-disable-line react-hooks/exhaustive-deps
};

export const usePrevious = value => {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref = useRef();

  // Store current value in ref
  useEffect(() => {
    ref.current = value;
    return () => {
      ref.current = undefined;
    };
  }, [value]); // Only re-run if value changes

  // Return previous value (happens before update in useEffect above)
  return ref.current;
};

export type PnlCategoryDefinition = {
  category: PnlCategoriesEnum | string;
  label: string;
  order: number;
};

export const QUALITY_LOSS_CATEGORY: PnlCategoryDefinition = {
  order: -1,
  category: PnlCategoriesEnumWithOptionalCategories.quality,
  label: PnlCategoryLabelsEnum.quality,
};
export const LOSS_CATEGORIES: PnlCategoryDefinition[] = [
  {
    order: 1,
    category: PnlCategoriesEnum.waiting,
    label: PnlCategoryLabelsEnum.waiting,
  },
  {
    order: 2,
    category: PnlCategoriesEnum.unplanned,
    label: PnlCategoryLabelsEnum.unplanned,
  },
  {
    order: 3,
    category: PnlCategoriesEnum.unknown,
    label: PnlCategoryLabelsEnum.unknown,
  },
];
export const LOSS_CATEGORIES_STRINGS = LOSS_CATEGORIES.map(
  ({ category }) => category
);
export const LOSS_CATEGORIES_SORTED = [...LOSS_CATEGORIES].sort(
  (a, b) => a.order - b.order
);
export const PROFIT_CATEGORIES: PnlCategoryDefinition[] = [
  {
    order: 1,
    category: PnlCategoriesEnum.producing,
    label: PnlCategoryLabelsEnum.producing,
  },
  {
    order: 2,
    category: PnlCategoriesEnum.planned,
    label: PnlCategoryLabelsEnum.planned,
  },
  {
    order: 3,
    category: PnlCategoriesEnum.notScheduled,
    label: PnlCategoryLabelsEnum.notScheduled,
  },
  {
    order: 4,
    category: PnlCategoriesEnum.uncollected,
    label: PnlCategoryLabelsEnum.uncollected,
  },
];
export const PROFIT_CATEGORIES_STRINGS = PROFIT_CATEGORIES.map(c => c.category);
export const PROFIT_CATEGORIES_SORTED = [...PROFIT_CATEGORIES].sort(
  (a, b) => a.order - b.order
);
export const BUTTERFLY_LEFT_WING_CATEGORIES = [
  {
    category: PnlCategoriesEnum.producing,
    label: PnlCategoryLabelsEnum.producing,
  },
  {
    category: PnlCategoriesEnum.planned,
    label: PnlCategoryLabelsEnum.planned,
  },
];
export const BUTTERFLY_RIGHT_WING_CATEGORIES = [
  {
    category: PnlCategoriesEnum.waiting,
    label: PnlCategoryLabelsEnum.waiting,
  },
  {
    category: PnlCategoriesEnum.unplanned,
    label: PnlCategoryLabelsEnum.unplanned,
  },
  {
    category: PnlCategoriesEnum.unknown,
    label: PnlCategoryLabelsEnum.unknown,
  },
  {
    category: PnlCategoriesEnum.notScheduled,
    label: PnlCategoryLabelsEnum.notScheduled,
  },
  {
    category: PnlCategoriesEnum.uncollected,
    label: PnlCategoryLabelsEnum.uncollected,
  },
];

export const ALL_PNL_CATEGORIES = [...LOSS_CATEGORIES, ...PROFIT_CATEGORIES];
export const ALL_PNL_CATEGORIES_STRINGS = ALL_PNL_CATEGORIES.map(
  c => c.category
);
export const CATEGORY_TO_LABEL_MAP = [
  ...LOSS_CATEGORIES,
  ...PROFIT_CATEGORIES,
].reduce(
  (acc, { category, label }) => ({ ...acc, [category]: label }),
  {} as {
    [pnlCategory: string]: string;
  }
);

export const PNL_COLORS = {
  producing: producingColor,
  waiting: waitingColor,
  unplanned: unplannedColor,
  planned: plannedColor,
  changeoverDowntime: changeoverColor,
  productionLoss: plantLossColor,
  notScheduled: plantLossColor,
  qualityLoss: qualityLossColor,
  quality: qualityLossColor,
  speedGain: darkGreen,
  speedLoss: untaggedColor,
  unknown: untaggedColor,
  uncollected: uncollectedColor,
};

export const PRODUCING_CHART_TITLES = {
  effectiveProducingTime: "Effective Producing Time",
  slowdownLoss: "Slowdown Loss",
  slowdownLossAndMinorStops: "Slowdown Loss and Minor Stops",
  qualityLoss: "Quality Loss",
  qualityLossWipIncrease: "Quality Loss + WIP Increase",
  minorStopLoss: "Minor Stop Loss",
  unknownMinorStopTime: "Unknown Minor Stop Time",
};

export const PNL_TEXT_COLORS = {
  producing: textOnProducing,
  waiting: textOnWaiting,
  unplanned: textOnUnplanned,
  planned: textOnPlanned,
  productionLoss: textOnUnplanned,
  notScheduled: textOnNotScheduled,
  qualityLoss: textOnUnplanned,
  speedGain: textOnUnplanned,
  speedLoss: textOnUnplanned,
  unknown: textOnUnknown,
  uncollected: textOnUncollected,
};

export const PNL_ORDER = {
  quality: -1,
  producing: 0,
  speedGain: 1,
  speedLoss: 2,
  planned: 4,
  unplanned: 5,
  waiting: 6,
  unknown: 7,
  productionLoss: 8,
  notScheduled: 9,
  uncollected: 10,
};

export const SORTED_STANDARD_PNL_CATEGORIES = [
  ...Object.values(PnlCategoriesEnum),
].sort((a, b) => (PNL_ORDER[a] < PNL_ORDER[b] ? -1 : 1));

export const LOSS_CATEGORIES_STRINGS_SORTED = [...LOSS_CATEGORIES_STRINGS].sort(
  (a, b) => PNL_ORDER[a] - PNL_ORDER[b]
);
export const PROFIT_CATEGORIES_STRINGS_REVERSED = [
  ...PROFIT_CATEGORIES_STRINGS,
].reverse();
export const LOSS_CATEGORIES_STRINGS_REVERSED = [
  ...LOSS_CATEGORIES_STRINGS,
].reverse();

export const BOX_SHADOW_TOOLTIP = `4px 7px 6px 0px rgba(0, 0, 0, 0.14), -2px 5px 10px 3px rgba(0, 0, 0, 0.04), 3px -3px 12px 6px rgba(0, 0, 0, 0.05)`;
export const BOX_SHADOW_ALERT = ` 0 1px 3px 0 rgba(60,64,67,0.302), 0 4px 8px 3px rgba(60,64,67,0.149)`;

export const Z_INDEX = {
  L12: 12,
  L11: 11,
  // levels above 10 are reserved for internal features like FlightDebugger and ComponentNamesTooltip
  L10: 10,
  L9: 9,
  L8: 8,
  L7: 7,
  L6: 6,
  L5: 5,
  L4: 4,
  L3: 3,
  L2: 2,
  L1: 1,
};

type CardMarginsType = {
  mobile: number;
} & {
  [PageName in keyof typeof PageNameEnum]: number;
};

/** margins for each page, between cards */
export const CARD_MARGINS: CardMarginsType = {
  [PageNameEnum.customerTiles]: 10,
  [PageNameEnum.processTiles]: 10,
  [PageNameEnum.groupTiles]: 10,
  [PageNameEnum.gridLayout]: 5,
  [PageNameEnum.operator]: 10,
  [PageNameEnum.timeline]: 10,
  [PageNameEnum.trends]: 10,
  [PageNameEnum.hublet]: 10,
  [PageNameEnum.settings]: 10,
  [PageNameEnum.help]: 10,
  [PageNameEnum.reports]: 10,
  [PageNameEnum.unknown]: 10,
  [PageNameEnum.schedules]: 10,
  [PageNameEnum.powerbi]: 10,
  // for simplicity mobile sizes are the same on all pages
  mobile: 5,
};

export const RANGESELECTOR_WIDTH_MOBILE = 247;
export const RANGESELECTOR_WIDTH_TABLET = 362;

export const TILE_WIDTH_PX_LG = 215;
export const TILE_WIDTH_PX_SM = 107;

export const GRID_GAP = 10;
export const NAV_HEIGHT = "2.65em";
export const NAV_HEIGHT_PX = 64;
export const NAV_HEIGHT_MOBILE_PX = NAV_HEIGHT_PX - 8;
export const repeatAutoFit = (repeatWidth: number) =>
  `repeat(auto-fit, ${repeatWidth}px)`;

// Timeline Constants
export const LEFT_PADDING = 30;
export const ROW_HEIGHT = 40;
export const SHRINK_TIMELINE_HEIGHT = ROW_HEIGHT / 2 - 2;
export const TIER0_ROW_HEIGHT = 42;
export const SELECTED_SEGMENT_BORDER_WIDTH = 3;
export const SEGMENT_SEPARATION_SPACE_WIDTH = 2;
// export const CELL_WIDTH = 70;
export const ROW_PADDING = 2;
export const COLUMN_PADDING = 0;
export const TOP_PADDING = 10;
export const TOP_PADDING_FOCUS = 32;
export const MODAL_WIDTH = 310;
export const MODAL_HEIGHT_NO_TAG = 115 + 15 * 7; // added 7 more small sized lines
export const MODAL_HEIGHT_WITH_TAG = MODAL_HEIGHT_NO_TAG + 15; // add one more line
export const MODAL_OFFSET_TOP_WITHOUT_TAG = 30;
export const MODAL_ARROW_HEIGHT = 18;
export const MODAL_PADDING = 8;
export const TICK_FONT_SIZE = 13;
// modal arrow is rotated 45deg -- a^2 + b^2 = c^2
export const MODAL_ARROW_OFFSET_LEFT =
  Math.pow(2 * Math.pow(MODAL_ARROW_HEIGHT, 2), 0.5) + MODAL_PADDING;

export const FONT_SIZE_EXTRA_SMALL = 12; // ? should I move these lines to the top of constants?
export const FONT_SIZE_SMALLER = 14;
export const FONT_SIZE_SMALL = 18;
export const FONT_SIZE_MEDIUM = 24;
export const FONT_SIZE_LARGE = 30;
export const FONT_SIZE_EXTRA_LARGE = 40;

export const SCROLLBAR_WIDTH_PX = 16;

export const BREAKPOINT_MOBILE = 480;
export const BREAKPOINT_MUI_NAV_SHRINK_8_PX = 600;
export const BREAKPOINT_TABLET = 768;
export const BREAKPOINT_TABTOP = 1024;
export const BREAKPOINT_DESKTOP = 1200;
export const BREAKPOINT_DESKTOP_LG = 1600;
export const BREAKPOINT_DESKTOP_XL = 2000;
export const BREAKPOINT_NAV_HEIGHT_RESIZE = 660;

export const useBreakpoints = () => {
  const isMobileOrLarger = useMediaQuery(`(min-width: ${BREAKPOINT_MOBILE}px)`);
  const isTabletOrLarger = useMediaQuery(`(min-width: ${BREAKPOINT_TABLET}px)`);
  const isDesktopOrLarger = useMediaQuery(
    `(min-width: ${BREAKPOINT_DESKTOP}px)`
  );
  const isDesktopXlOrLarger = useMediaQuery(
    `(min-width: ${BREAKPOINT_DESKTOP_XL}px)`
  );

  /** "sm","md", or "lg" depending on breakpoint (sm: <mobile, md: <tablet, lg: >tablet) */
  const smMdLg = isTabletOrLarger ? "lg" : isMobileOrLarger ? "md" : "sm";
  return {
    isMobileOrLarger,
    isTabletOrLarger,
    isDesktopOrLarger,
    isDesktopXlOrLarger,
    smMdLg,
  };
};

export const MEDIAQ_TABLET = `@media (min-width: ${BREAKPOINT_MOBILE}px)`;
export const MEDIAQ_DESKTOP = `@media (min-width: ${BREAKPOINT_TABLET}px)`;

/** border radius is specified for cards (containing elements) and elements (inside of cards) */
const BORDER_RADIUS = {
  desktop: { element: 6, card: 10 },
  mobile: { element: 4, card: 4 },
};

export const ELEMENT_BORDER_RADIUS_CSS = `
border-radius: ${BORDER_RADIUS.mobile.element}px;
${MEDIAQ_TABLET} {
  border-radius: ${BORDER_RADIUS.desktop.element}px;
}
`;
export const CARD_BORDER_RADIUS_CSS = `
border-radius: ${BORDER_RADIUS.mobile.card}px;
${MEDIAQ_TABLET} {
  border-radius: ${BORDER_RADIUS.desktop.card}px;
}
`;

export const CARD_INNER_PADDING = 16; // 16px = 1em

export const GROUP_METRICS_VERTICAL_PADDING = 10;

export const MACHINES_COLUMN_FONT_SIZE = 9;
export const MACHINES_COLUMN_WIDTH_MOBILE = 70;
export const MACHINES_COLUMN_WIDTH_TABLET = 100;

export const CENTER_METRIC_TITLE_PADDING_BOTTOM = 12;

export const CURRENT_SHIFT_WIDTH = 165;

export const LOGO_HEIGHT = 56;
export const LOGO_WIDTH = 125;

export const BUTTERFLY_METRIC_TITLE_WIDTH = 90;
export const BABY_BUTTERFLY_MARGIN = 30;

export const SHINY_ANIMATED = css`
  overflow: hidden;
  &:after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    transform: translateX(100%);
    z-index: 1;
    animation: slide 1s infinite 3s;
    /* CSS Gradient - complete browser support from http://www.colorzilla.com/gradient-editor/ */
    background: -moz-linear-gradient(
      left,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.8) 50%,
      rgba(128, 186, 232, 0) 99%,
      rgba(125, 185, 232, 0) 100%
    ); /* FF3.6+ */
    background: -webkit-gradient(
      linear,
      left top,
      right top,
      color-stop(0%, rgba(255, 255, 255, 0)),
      color-stop(50%, rgba(255, 255, 255, 0.8)),
      color-stop(99%, rgba(128, 186, 232, 0)),
      color-stop(100%, rgba(125, 185, 232, 0))
    ); /* Chrome,Safari4+ */
    background: -webkit-linear-gradient(
      left,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.8) 50%,
      rgba(128, 186, 232, 0) 99%,
      rgba(125, 185, 232, 0) 100%
    ); /* Chrome10+,Safari5.1+ */
    background: -o-linear-gradient(
      left,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.8) 50%,
      rgba(128, 186, 232, 0) 99%,
      rgba(125, 185, 232, 0) 100%
    ); /* Opera 11.10+ */
    background: -ms-linear-gradient(
      left,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.8) 50%,
      rgba(128, 186, 232, 0) 99%,
      rgba(125, 185, 232, 0) 100%
    ); /* IE10+ */
    background: linear-gradient(
      to right,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.8) 50%,
      rgba(128, 186, 232, 0) 99%,
      rgba(125, 185, 232, 0) 100%
    ); /* W3C */
    filter: progid:DXImageTransform.Microsoft.gradient( startColorstr='#00ffffff', endColorstr='#007db9e8',GradientType=1 ); /* IE6-9 */
  }
`;

export const OUT_OF_FOCUS_OPACITY = 0.35;

export const getIsCypressRunning = () =>
  "Cypress" in window || "Cypress" in document;

export const MINIMUM_LOADED_PERCENT_THRESHOLD = 0.5; // percent of data that must be loaded before "loading" indicators disappear

export const TRACING_HEADERS = { "trace-flags": 1, "X-B3-Sampled": true };

export const getIsIe11 = (): boolean => {
  const browserInfo = getBrowserInfo();
  return (
    browserInfo.browser.name === "IE" && browserInfo.browser.version === "11.0"
  );
};
function getIsChrome() {
  return (
    navigator.userAgent.includes("Chrome") &&
    navigator.vendor.includes("Google Inc")
  );
}
export function getIsSafari() {
  const browserInfo = getBrowserInfo();
  const isIOS = browserInfo.os.name === "iOS";

  // Special case: Treat Chrome on iOS like Safari (last I checked, "chrome" on iOS is just a wrapper around Safari)
  const isCriOS = browserInfo.browser.name === "Chrome" && isIOS;
  if (isCriOS) {
    return true;
  }

  const isChrome = getIsChrome();
  if (isChrome) {
    return false;
  }

  const isSafari = browserInfo.browser.name === "Safari";
  // Check for macOS Safari user agent string
  const isMacOSSafari =
    /Macintosh.*AppleWebKit\/\d+\.\d+.*\(KHTML, like Gecko\)/.test(
      window.navigator.userAgent
    );

  return isSafari || isIOS || isMacOSSafari;
}

export const EXPAND_ICON_TRANSITION =
  "all 0.3s cubic-bezier(0.075, 0.82, 0.165, 1)";

export const CHANGEOVER_DOWNTIME = "changeoverDowntime";

export const LD_CLIENT_SIDE_ID = "5ea1e82947fec20a4e85b698";

export const CHART_MAX_WIDTH = 900;

export const AUTH_PROFILE_NAME = "profile";
export const SPACE_BETWEEN = "space-between";
export const AHEAD_BEHIND_OUTPUT = "AHEAD_BEHIND_OUTPUT";

/** if a Timeline page has more than this many processes, don't load the Timelines at first, to improve overall performance */
export const MAX_TIMELINES_BEFORE_HIDE_INITIALLY = 10;

/** proces roles - if the process has one of these roles, show a decorator above the tile in ProcessTilesPanel */
export enum LineRolesEnum {
  LEADER = "LEADER",
  CONSTRAINT = "CONSTRAINT",
  STANDARD = "STANDARD",
}
/** height of the LineRole decorator in ProcessTilesPanel > ProcessTile */
export const TILE_DECORATOR_HEIGHT = 30;

export const LANGUAGES = {
  en: {
    value: "en",
    text: "English",
  },
  fr: {
    value: "fr",
    text: "Français",
  },
  "fr-CA": {
    value: "fr-CA",
    text: "Français (Canadien)",
  },
  "es-419": {
    value: "es-419",
    text: "Español (Latinoamérica)",
  },
};

export function getCkeyFromWindow() {
  const pathnameParams = window.location.pathname.split("/").slice(1);
  // e.g. pathnameParams = ["px","dashboard","all","01"]
  const ckeyFromWindow = pathnameParams[0];
  return ckeyFromWindow;
}
export const TOO_MANY_RECENT_FAILURES_BREAKER_OPEN =
  "TooManyRecentFailuresBreakerOpen";

export const REPORTS_PAGE_MIN_DATE = 1578893400000; // JAN_13TH_2020

export const MAGIC_STRING = "raven-employees";

/** these strings sometimes appear in the ckey slot during fetch requests but should be ignored/skipped */
export const INVALID_CUSTOMERS = ["callback"];
